export interface MapCamera {
  center: [number, number];
  zoom?: number;
}

export interface MapUrlState {
  regions: string[];
  pipelines: string[];
  segments: string[];
  camera?: MapCamera;
  zone?: string;
}

export const MAP_URL_KEYS = {
  region: 'region',
  pipeline: 'pipeline',
  segment: 'segment',
  center: 'center',
  zoom: 'zoom',
  zone: 'zone',
} as const;

export const emptyMapUrlState: MapUrlState = { regions: [], pipelines: [], segments: [] };

const HASH_PREFIX = 'sp';
const RAIL_VISIBLE = 'r';

export function queryOfWidgetPath(widgetPath?: string | null): string {
  return widgetPath?.split('?')[1] ?? '';
}

export function mapWidgetPath(state: MapUrlState, widgetPath?: string | null): string {
  const base = widgetPath?.split('?')[0] ?? '';
  const query = writeMapUrlState(new URLSearchParams(), state).toString();
  return query ? `${base}?${query}` : base;
}

export function mapShareUrl(
  state: MapUrlState,
  location: { origin: string; pathname: string; search: string; hash: string },
  appId: string,
  widgetPath?: string | null,
): string {
  const segments = location.hash.replace(/^#/, '').split(':');
  const onSidePanelHash = segments[0] === HASH_PREFIX && !!segments[1];

  const rail = onSidePanelHash && segments[2] ? segments[1] : RAIL_VISIBLE;
  const app = onSidePanelHash
    ? segments[2]
      ? segments[2]
      : segments[1]
    : encodeURIComponent(appId);

  const path = mapWidgetPath(state, widgetPath);
  const pathSegment = path ? `:${encodeURIComponent(path)}` : '';

  return `${location.origin}${location.pathname}${location.search}#${HASH_PREFIX}:${rail}:${app}${pathSegment}`;
}

const CENTER_PRECISION = 5;
const ZOOM_PRECISION = 2;

const round = (value: number, places: number) => Number(value.toFixed(places));

function readCamera(params: URLSearchParams): MapCamera | undefined {
  const raw = params.get(MAP_URL_KEYS.center);
  if (!raw) return undefined;

  const [lng, lat] = raw.split(',').map(Number);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return undefined;
  if (Math.abs(lng) > 180 || Math.abs(lat) > 90) return undefined;

  const rawZoom = params.get(MAP_URL_KEYS.zoom);
  const zoom = rawZoom === null ? NaN : Number(rawZoom);

  return { center: [lng, lat], ...(Number.isFinite(zoom) ? { zoom } : {}) };
}

export function readMapUrlState(params: URLSearchParams): MapUrlState {
  const camera = readCamera(params);
  const zone = params.get(MAP_URL_KEYS.zone);

  return {
    regions: params.getAll(MAP_URL_KEYS.region).filter(Boolean),
    pipelines: params.getAll(MAP_URL_KEYS.pipeline).filter(Boolean),
    segments: params.getAll(MAP_URL_KEYS.segment).filter(Boolean),
    ...(camera ? { camera } : {}),
    ...(zone ? { zone } : {}),
  };
}

export function writeMapUrlState(current: URLSearchParams, state: MapUrlState): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const key of Object.values(MAP_URL_KEYS)) next.delete(key);

  state.regions.forEach(id => next.append(MAP_URL_KEYS.region, id));
  state.pipelines.forEach(id => next.append(MAP_URL_KEYS.pipeline, id));
  state.segments.forEach(id => next.append(MAP_URL_KEYS.segment, id));

  if (state.camera) {
    const [lng, lat] = state.camera.center;
    next.set(
      MAP_URL_KEYS.center,
      `${round(lng, CENTER_PRECISION)},${round(lat, CENTER_PRECISION)}`,
    );
    if (state.camera.zoom !== undefined) {
      next.set(MAP_URL_KEYS.zoom, String(round(state.camera.zoom, ZOOM_PRECISION)));
    }
  }

  if (state.zone) next.set(MAP_URL_KEYS.zone, state.zone);

  return next;
}
